import React, { useMemo } from 'react';
import { CardDefault, Text, TagColored, TooltipTrigger } from '@spark';
import { formatScore } from '@/shared/utils/metricFormatting';
import {
  getImprovementMeta,
  buildQualityMetricRows,
  HeaderCell,
  BenchmarkSubtitleCell,
  EmptySubtitleCell,
  MetricNameCell,
} from './QualityMetrics';
import './QualityMetricsVersionComparison.css';

/**
 * Quality Metrics view for a SpecificAI-version vs SpecificAI-version
 * comparison (Figma node 5147:12746). Three bordered tables:
 *   1. metric names,
 *   2. the compared (lower) version evaluated on its OWN benchmark,
 *   3. a grouped table: compared model on the anchor benchmark, anchor model
 *      on the anchor benchmark, and the improvement between those two.
 */

const ValueCell = ({ value, loading }) => {
  if (loading) {
    return <Text variant="base-regular" color="secondary">…</Text>;
  }
  const formatted = formatScore(value, { decimals: 3 });
  if (formatted === null) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  const exact = formatScore(value, { decimals: 5 }) ?? formatted;
  return (
    <TooltipTrigger text={`Exact value: ${exact}`}>
      <span>
        <Text variant="base-regular" color="primary">{formatted}</Text>
      </span>
    </TooltipTrigger>
  );
};

const ImprovementCell = ({ improvement }) => {
  const meta = getImprovementMeta(improvement);
  if (meta.kind === 'empty' || meta.kind === 'neutral') {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  const variant = meta.kind === 'positive' ? 'cyan' : 'volcano';
  const iconSlug = meta.kind === 'positive' ? 'trending-up' : 'trending-down';
  return (
    <span>
      <TagColored label={meta.text} variant={variant} showPrefixIcon prefixIconSlug={iconSlug} />
    </span>
  );
};

const QualityMetricsVersionComparison = ({
  metrics,
  /** Map metric key -> score of the compared model on its own benchmark. */
  ownBenchmarkValues = null,
  ownBenchmarkLoading = false,
  compareVersionLabel,
  compareBenchmarkLabel,
  compareModelLabel,
  anchorVersionLabel,
  anchorBenchmarkLabel,
  anchorModelLabel,
  /** Opens the Versions Diff modal; renders the header button when provided. */
  onViewVersionsDiff = null,
}) => {
  const rows = useMemo(() => buildQualityMetricRows(metrics), [metrics]);

  // `type="default"` (not `collapsed`): the header hosts the View Versions
  // Diff secondary button, which CardDefault only renders for non-collapsible
  // cards. The card was always rendered open anyway.
  const headerButtonProps = {
    showSecondaryButton: Boolean(onViewVersionsDiff),
    secondaryLabel: 'View Versions Diff',
    secondaryIconSlug: 'diff',
    secondaryButtonSize: 'medium',
    onSecondaryClick: onViewVersionsDiff ?? undefined,
  };

  if (rows.length === 0) {
    return (
      <CardDefault
        className="fs-unmask"
        type="default"
        fullWidth
        title="Quality Metrics"
        showSubtitle={false}
        border="yes"
        {...headerButtonProps}
      >
        <div className="quality-metrics-card-body">
          <Text variant="base-regular" color="secondary">No metrics available</Text>
        </div>
      </CardDefault>
    );
  }

  return (
    <CardDefault
      className="fs-unmask"
      type="default"
      fullWidth
      title="Quality Metrics"
      showSubtitle={false}
      border="yes"
      {...headerButtonProps}
    >
      <div className="quality-metrics-card-body">
        <div className="qmvc-body qmvc-body--metric-descriptions">
          {/* Metric names */}
          <div className="qmvc-table qmvc-table--names">
            <HeaderCell caption="" />
            <EmptySubtitleCell />
            {rows.map((row) => (
              <MetricNameCell metricKey={row.key} label={row.metric} key={`name-${row.key}`} />
            ))}
          </div>
          {/* Compared version on its own benchmark */}
          <div className="qmvc-table qmvc-table--own">
            <HeaderCell caption={compareVersionLabel} />
            <BenchmarkSubtitleCell
              benchmarkLabel={compareBenchmarkLabel}
              modelLabel={compareModelLabel}
            />
            {rows.map((row) => (
              <div className="qmvc-cell" key={`own-${row.key}`}>
                <ValueCell
                  value={ownBenchmarkValues?.[row.key]}
                  loading={ownBenchmarkLoading}
                />
              </div>
            ))}
          </div>
          {/* Grouped: both models on the anchor benchmark + improvement */}
          <div className="qmvc-group">
            <div className="qmvc-col">
              <HeaderCell caption={compareVersionLabel} />
              <BenchmarkSubtitleCell
                benchmarkLabel={anchorBenchmarkLabel}
                modelLabel={compareModelLabel}
              />
              {rows.map((row) => (
                <div className="qmvc-cell" key={`cmp-${row.key}`}>
                  <ValueCell value={row.oldModel} />
                </div>
              ))}
            </div>
            <div className="qmvc-col">
              <HeaderCell caption={anchorVersionLabel} />
              <BenchmarkSubtitleCell
                benchmarkLabel={anchorBenchmarkLabel}
                modelLabel={anchorModelLabel}
              />
              {rows.map((row) => (
                <div className="qmvc-cell" key={`anchor-${row.key}`}>
                  <ValueCell value={row.newModel} />
                </div>
              ))}
            </div>
            <div className="qmvc-col">
              <HeaderCell caption="Improvement" />
              <EmptySubtitleCell />
              {rows.map((row) => (
                <div className="qmvc-cell" key={`imp-${row.key}`}>
                  <ImprovementCell improvement={row.improvement} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </CardDefault>
  );
};

export default QualityMetricsVersionComparison;
